
import { useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SupabaseNotification } from "@/types/notifications";
import { loadPreferences, savePreferences } from "@/utils/notificationPreferences";
import { useDebounce } from "@/hooks/useDebounce";

const SORT_PARAM_KEY = "sort";
const SORT_DIRECTION_PARAM_KEY = "dir";

const isSortDirection = (value: string | null): value is "asc" | "desc" =>
  value === "asc" || value === "desc";

/**
 * Hook for filtering and sorting notifications.
 *
 * URL hydration from cache/returnSearch happens centrally in
 * useNotificationsUrlHydration (Notifications.tsx). This hook only reads
 * from the URL, with a cache fallback for the first render before hydration.
 */
export const useNotificationsFiltering = (notifications: SupabaseNotification[] = []) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const cachedPreferences = loadPreferences();

  const searchTerm = searchParams.get("q") ?? "";
  const sortKey = searchParams.get(SORT_PARAM_KEY) ?? cachedPreferences.sortKey;
  const sortDirection = isSortDirection(searchParams.get(SORT_DIRECTION_PARAM_KEY))
    ? searchParams.get(SORT_DIRECTION_PARAM_KEY)
    : cachedPreferences.sortDirection;

  const setSearchTerm = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set("q", value);
      else next.delete("q");
      return next;
    }, { replace: true });
    // Bewust géén savePreferences voor searchTerm — q is vluchtig en alleen URL-driven.
  };

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const sortConfig = useMemo(
    () => ({
      key: sortKey,
      direction: sortDirection as "asc" | "desc"
    }),
    [sortDirection, sortKey]
  );

  // Filter notifications based on debounced search term
  const filteredNotifications = useMemo(() => {
    if (!debouncedSearchTerm) return notifications;
    
    return notifications.filter(notification => {
      const searchTermLower = debouncedSearchTerm.toLowerCase();
      return (
        ((notification.vo_number || "").toLowerCase().includes(searchTermLower)) ||
        ((notification.message || "").toLowerCase().includes(searchTermLower)) ||
        ((notification.type || "").toLowerCase().includes(searchTermLower)) ||
        // Add additional search fields for better matching
        ((notification.exact_order_number || "").toLowerCase().includes(searchTermLower)) ||
        ((notification.user_name || "").toLowerCase().includes(searchTermLower))
      );
    });
  }, [notifications, debouncedSearchTerm]);

  // Sort filtered notifications
  const sortedNotifications = useMemo(() => {
    return [...filteredNotifications].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof SupabaseNotification];
      const bValue = b[sortConfig.key as keyof SupabaseNotification];
      
      if (sortConfig.key === "created_at" || sortConfig.key === "updated_at") {
        return sortConfig.direction === "asc" 
          ? new Date(aValue as string).getTime() - new Date(bValue as string).getTime()
          : new Date(bValue as string).getTime() - new Date(aValue as string).getTime();
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      }
      
      return 0;
    });
  }, [filteredNotifications, sortConfig]);

  useEffect(() => {
    savePreferences({
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction
    });
  }, [sortConfig]);

  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(SORT_PARAM_KEY, key);
      next.set(SORT_DIRECTION_PARAM_KEY, direction);
      return next;
    }, { replace: true });
  };

  return {
    searchTerm,
    setSearchTerm,
    filteredNotifications,
    sortedNotifications,
    sortConfig,
    requestSort
  };
};
